import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-tnn-warm border border-tnn-ink/12 rounded-xl px-4 py-3 text-sm text-tnn-ink focus:outline-none focus:border-tnn-purple focus:ring-2 focus:ring-tnn-purple/15 transition-[border-color,box-shadow] duration-200";

  return (
    <div className="min-h-screen bg-tnn-warm flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        <Link
          to="/"
          data-testid="admin-back-home"
          className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.18em] uppercase text-tnn-muted hover:text-tnn-purple transition-colors duration-200 mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> tnnafrica.org
        </Link>
        <div className="bg-white border border-tnn-ink/8 rounded-3xl p-8 sm:p-10">
          <img src="/assets/logo.png" alt="The Nutrition Network Africa" className="h-10 w-auto" />
          <h1 className="mt-6 font-display text-2xl font-bold text-tnn-ink">Admin Sign In</h1>
          <p className="mt-1 text-sm text-tnn-muted">Partnership enquiries dashboard</p>
          <form onSubmit={onSubmit} className="mt-7 space-y-5" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="block text-[11px] font-bold tracking-[0.18em] uppercase text-tnn-purple mb-2">
                Email
              </label>
              <input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="block text-[11px] font-bold tracking-[0.18em] uppercase text-tnn-purple mb-2">
                Password
              </label>
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
              />
            </div>
            {error && (
              <p className="text-sm text-red-700" data-testid="admin-login-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-tnn-purple text-white font-semibold text-sm py-3.5 rounded-full transition-colors duration-200 hover:bg-tnn-deep disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
